import { MenuOutlined } from "@ant-design/icons";
import { DndContext, DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
	restrictToHorizontalAxis,
	restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
	SortableContext,
	horizontalListSortingStrategy,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Table } from "antd";
import { DIRECTION_TYPE } from "constants/constants";
import React from "react";

type Direction = (typeof DIRECTION_TYPE)[keyof typeof DIRECTION_TYPE];

interface DraggableTableProps extends React.ComponentProps<typeof Table>{
	dragDirection: Direction;
	canDrag?: boolean;
	rowKey: string;
	columns: any;
	dataSource: any[];
	onDragEnd: ({ active, over }: DragEndEvent) => void;
	onDragStart?: (event: DragStartEvent) => void;
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
	"data-row-key": string;
}

const Row = ({ children, ...props }: RowProps) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: props["data-row-key"],
	});

	const style: React.CSSProperties = {
		...props.style,
		transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
		transition,
		...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
	};

	return (
		<tr {...props} ref={setNodeRef} style={style} {...attributes}>
			{React.Children.map(children, child => {
				if ((child as React.ReactElement).key === "sort") {
					return React.cloneElement(child as React.ReactElement, {
						children: (
							<div
								ref={setActivatorNodeRef}
								style={{ touchAction: "none", cursor: "move" }}
								{...listeners}
								className="flex items-center justify-center py-8"
							>
								<MenuOutlined />
							</div>
						),
					});
				}
				return child;
			})}
		</tr>
	);
};

const DraggableTable = ({
	dragDirection = DIRECTION_TYPE.HORIZONTAL,
	rowKey,
	canDrag = true,
	columns,
	dataSource,
	onDragEnd,
	onDragStart,
	...props
}: DraggableTableProps) => (
	<DndContext
	modifiers={[
		dragDirection === DIRECTION_TYPE.HORIZONTAL
			? restrictToHorizontalAxis
			: restrictToVerticalAxis,
	]}
	onDragEnd={onDragEnd}
	onDragStart={onDragStart}
>
	<SortableContext
		items={dataSource.map(record => record[rowKey])}
		strategy={
			dragDirection === DIRECTION_TYPE.HORIZONTAL
				? horizontalListSortingStrategy
				: verticalListSortingStrategy
		}
		disabled={!canDrag}
	>
		<Table
			bordered
			columns={columns}
			dataSource={dataSource}
			rowKey={(record: any) => record[rowKey]}
			components={{
				body: {
					row: Row,
				},
			}}
			{...props}
		/>
	</SortableContext>
</DndContext>
);
export default DraggableTable;
